import { IconButton, Tooltip, useDisclosure } from '@chakra-ui/react'
import { Edit } from 'src/components/icon'
import { useSpinner } from 'src/hooks/use-spinner'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import {
  CancelExplanationReservationDocument,
  ExplanationReservationInviteeType,
  ExplanationType,
  UpdateExplanationReservationDocument,
} from 'src/lib/gql-client'
import { useGqlMutation } from 'src/lib/gql-client/request'

import { EditReservationModal } from '../EditReservationModal/EditReservationModal'

type Props = {
  explanationType: ExplanationType
  explanationReservationUid: string
  inviteeType: ExplanationReservationInviteeType
  reservedAt: string
  isAgreementCompleted: boolean
  isPartnerMemberNotAssigned?: boolean
  partnerHospitalName: string | undefined
  mutateExplanation: () => void
}

export const UpdateReservationButton: React.FC<Props> = ({
  explanationType,
  explanationReservationUid,
  inviteeType,
  reservedAt,
  isPartnerMemberNotAssigned,
  isAgreementCompleted,
  partnerHospitalName,
  mutateExplanation,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const toast = useMirohaToast()

  const { showSpinner, hideSpinner } = useSpinner()

  const { request: updateReservation } = useGqlMutation(
    UpdateExplanationReservationDocument,
    {
      onRequestStarted: showSpinner,
      onRequestDone: hideSpinner,
      onSuccess: () => {
        onClose()
        mutateExplanation()
        const toastDescription = isAgreementCompleted
          ? 'ご予約のお時間に「ビデオ通話を開始」より面談を実施してください。'
          : 'ご予約のお時間に「説明ロビーへ」より面談を実施してください。'
        toast({
          status: 'success',
          title: '次回面談情報を送信しました。',
          description: toastDescription,
        })
      },
    },
  )

  const { request: cancelReservation } = useGqlMutation(
    CancelExplanationReservationDocument,
    {
      onRequestStarted: showSpinner,
      onRequestDone: hideSpinner,
      onSuccess: () => {
        mutateExplanation()
        toast({
          status: 'success',
          title: '面談予約を取り消しました。',
        })
      },
    },
  )

  return (
    <>
      <Tooltip label="編集">
        <IconButton
          variant="customIconButtonGhost"
          aria-label="面談予定変更"
          icon={<Edit />}
          onClick={onOpen}
        />
      </Tooltip>

      <EditReservationModal
        isOpen={isOpen}
        formType="edit"
        key={isOpen ? 'open' : 'close'} // 開いた時に初期値を設定するためにkeyを設定
        explanationType={explanationType}
        savedValue={{
          reservedAt,
          inviteeType,
        }}
        isAgreementCompleted={isAgreementCompleted}
        isPartnerMemberNotAssigned={isPartnerMemberNotAssigned ?? false}
        partnerHospitalName={partnerHospitalName}
        onClose={onClose}
        onSubmit={async ({ inviteeType, reservedAt }) => {
          await updateReservation({
            input: {
              explanationReservationUid,
              reservationInviteeType: inviteeType,
              reservedAt,
            },
          })
        }}
        onCancelReservation={async () => {
          onClose()
          await cancelReservation({
            input: {
              explanationReservationUid,
            },
          })
        }}
      />
    </>
  )
}
